import React from "react";
import { useRef, useState, useEffect } from "react";
import "./Agile.css";
import agile1 from "../assetsprogressive/agile1.png";
import agile2 from "../assetsprogressive/agile2.png";
import agile3 from "../assetsprogressive/agile3.png";
import agile4 from "../assetsprogressive/agile4.png";
import agile5 from "../assetsprogressive/agile5.png";
import agile6 from "../assetsprogressive/agile6.png";

const agileCards = [
  {
    id: 1,
    image: agile1,
    bg: "#CBDBF2",
    title: "Technical Discovery & Specification Analysis",
    description:
      "We assess user flows, APIs, caching needs, and integration requirements to define accurate PWA specifications, performance goals, and technical constraints before development begins.",
  },
  {
    id: 2,
    image: agile2,
    bg: "#FEE0CA",
    title: "UX Architecture & Interface Wireframing",
    description:
      "We build responsive wireframes, information architecture, and interaction flows aligned with PWA standards—fast rendering, smooth transitions, and an app-like interface across all devices.",
  },
  {
    id: 3,
    image: agile3,
    bg: "#eadff8",
    title: "PWA Architecture & Service Worker Planning",
    description:
      "We define caching logic, offline workflows, data sync rules, and security layers to ensure your PWA remains stable, scalable, and reliable in real-world network conditions.",
  },
  {
    id: 4,
    image: agile4,
    bg: "#d6f6fa",
    title: "Development & API Integration Layer",
    description:
      "We implement service workers, Web App Manifest, push alerts, install prompts, and background sync while integrating APIs and building performance-first front-end components.",
  },
  {
    id: 5,
    image: agile5,
    bg: "#FBD7CD",
    title: "Multi-Environment Testing & Performance Validation",
    description:
      "Your PWA is tested across devices, browsers, and network speeds with Lighthouse audits, offline simulations, and performance checks for metrics like LCP, FCP, and TTI.",
  },
  {
    id: 6,
    image: agile6,
    bg: "#C6E4C0",
    title: "Deployment, Monitoring & Performance Optimization",
    description:
      "We deploy your PWA, configure analytics, monitor service worker updates, and fine-tune caching, asset delivery, and UI responsiveness based on real user performance data.",
  },
];

export default function Agile() {
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [scrollSteps, setScrollSteps] = useState(agileCards.length);

  const getCardScrollWidth = () => {
    if (!scrollContainerRef.current) return 0;
    const card = scrollContainerRef.current.querySelector(".card");
    if (!card) return 0;
    const gap = 24;
    return card.offsetWidth + gap;
  };

  const getScrollSteps = () => {
    if (!scrollContainerRef.current) return agileCards.length;
    const { scrollWidth, clientWidth } = scrollContainerRef.current;
    const cardScrollWidth = getCardScrollWidth();
    if (cardScrollWidth === 0) return agileCards.length;
    return Math.ceil((scrollWidth - clientWidth) / cardScrollWidth) + 1;
  };

  const checkScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);

      const cardScrollWidth = getCardScrollWidth();
      if (cardScrollWidth > 0) {
        const index = Math.round(scrollLeft / cardScrollWidth);
        setActiveIndex(index);
      }

      setScrollSteps(getScrollSteps());
    }
  };

  useEffect(() => {
    checkScroll();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);
      return () => {
        container.removeEventListener("scroll", checkScroll);
        window.removeEventListener("resize", checkScroll);
      };
    }
  }, []);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 450;
      const newScrollLeft =
        direction === "left"
          ? scrollContainerRef.current.scrollLeft - scrollAmount
          : scrollContainerRef.current.scrollLeft + scrollAmount;

      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: "smooth",
      });
    }
  };

  return (
    <div id="progressive-agile">
      <div className="top">
        <h2 className="heading">
          Our Strategic Workflow for Future-Ready PWA Solutions
        </h2>
        <p className="subheading">
          Our strategic workflow keeps every project structured, transparent,
          and aligned with your goals. Through our progressive web app
          development services, we focus on building future-ready solutions that
          load fast, scale easily, and deliver consistent value to your users.
        </p>
      </div>

      <div className="slider-wrapper">
        {canScrollLeft && (
          <button
            onClick={() => scroll("left")}
            className="scroll-button scroll-button-left"
          >
            ←
          </button>
        )}

        <div
          ref={scrollContainerRef}
          className={`cards-container ${!canScrollLeft ? "padding-left" : ""} ${
            !canScrollRight ? "padding-right" : ""
          }`}
        >
          {agileCards.map((card) => (
            <div key={card.id} className="card" style={{ background: card.bg }}>
              <div className="card-container-main">
                <img src={card.image} alt={card.title} className="card-image" />
                <h2 className="card-title">{card.title}</h2>
                <p className="card-description">{card.description}</p>
              </div>
            </div>
          ))}
        </div>

        {canScrollRight && (
          <button
            onClick={() => scroll("right")}
            className="scroll-button scroll-button-right"
          >
            →
          </button>
        )}
      </div>

      <div className="custom-dots">
        {Array.from({ length: scrollSteps }).map((_, index) => (
          <button
            key={index}
            className={`dot ${index === activeIndex ? "active" : ""}`}
            onClick={() => {
              scrollContainerRef.current?.scrollTo({
                left: index * getCardScrollWidth(),
                behavior: "smooth",
              });
            }}
          />
        ))}
      </div>
    </div>
  );
}